import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {selectGoogleAnalyticsConfig, selectUserPermissions} from '../store/selectors';
import {filter} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';
import {GoogleAnalyticsConfig} from '../models/google-analytics-config.model';
import {UserPermissions} from '../models/user-permissions.model';
import {environment} from '../../environments/environment';
import {checkIfPlatformSite} from '../common/platform-utils';
import { runAfterUserInteraction, runWhenIdle } from '../common/idle-utils';
import { toSignal } from '@angular/core/rxjs-interop';

interface GoogleAnalyticsData {
  config: GoogleAnalyticsConfig;
  permissions: UserPermissions;
}

@Component({
    selector: "google-analytics-integration",
    template: "",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoogleAnalyticsIntegrationComponent{
  private store = inject<Store<AppState>>(Store);
  private router = inject(Router);


  private readonly initialized = signal(false);

  gaTrackingId: string;

  private readonly config = toSignal(this.store.select(selectGoogleAnalyticsConfig));
  private readonly permissions = toSignal(this.store.select(selectUserPermissions));

  private readonly navigationEnd = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    )
  );

  constructor() {
    effect(() => {
      const config = this.config();
      const permissions = this.permissions();

      if (permissions && !this.initialized()) {
        this.activateGoogleAnalyticsIntegration({config, permissions});
      }
    });

    effect(() => {
      const nav = this.navigationEnd();
      if (this.initialized() && nav) {
        runWhenIdle(() => this.reportPageView());
      }
    });
  }
  
  activateGoogleAnalyticsIntegration({config, permissions}: GoogleAnalyticsData) {  
    // if the user is an admin, use the platform tracking Id
    if (permissions.isAdmin || checkIfPlatformSite()) {
      this.gaTrackingId = environment?.google?.googleAnalyticsTrackingId;
      console.log(`Using platform Google Analytics Tracking Id ${this.gaTrackingId}`);
    }
    // otherwise, use the creator tracking Id, if one is available
    else if (config?.gaTrackingId && config.enabled) {
      this.gaTrackingId = config.gaTrackingId;
      console.log(`Using course creator Google Analytics Tracking Id ${this.gaTrackingId}`);
    }

    if (this.gaTrackingId) {
      runAfterUserInteraction(() => this.loadGoogleAnalyticsScript(this.gaTrackingId));
      this.initialized.set(true);
    }

  }

  loadGoogleAnalyticsScript(gaTrackingId:string) {

    // Begin Google Tag Manager
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j:any=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer',gaTrackingId);
    // End Google Tag Manager

  }

  reportPageView() {
    // report page view to Google Analytics
    const gaFn = (window as any)?.ga;
    if (typeof gaFn === 'function') {
      const path = location.pathname;
      console.log(`reporting page view for ${path} to Google Analytics property ${this.gaTrackingId}`);
      gaFn('set', 'page', path);
      gaFn('send', path);
    }
    else {
      console.log(`ga is undefined, cannot report page view for ${location.pathname}`);
      runAfterUserInteraction(() => {
        const retryGa = (window as any)?.ga;
        if (typeof retryGa === 'function') {
          const path = location.pathname;
          console.log(`reporting page view for ${path} to Google Analytics property ${this.gaTrackingId}`);
          retryGa('set', 'page', path);
          retryGa('send', path);
        }
      });
    }

    // report the page view to Google Tag Manager
    window['dataLayer'] = window['dataLayer'] || [];

    const gtmEvent = {
      'event': 'virtualPageview',
      'pageUrl': window?.location?.toString(),
      'pageTitle': document.title
    }
    console.log(`Reporting GTM event to GTM container ${this.gaTrackingId}`, gtmEvent);

    window['dataLayer'].push(gtmEvent);

  }

}
